<template>

  <component :is="el" :label="label" :align="align" :prop="prop" :required="required" :error="error">

    <span v-if="noForm && showLabel">{{label}}</span>

    <el-date-picker
      v-if="!disabled"
      :ref="ref"
      v-model="formData"
      :type="localType"
      :format="dateFormat"
      :value-format="valueFormat"
      :disabled="disabled"
      :placeholder="placeholder"
      :picker-options="dateOptions"
      :required="required"
      @change="updated">
    </el-date-picker>
    <b v-else>{{this.formatDate(formData)}}</b><!-- XOL-596: optional trailing slot INSIDE the form item (the request drawer's "changed underneath you" cue); nothing renders when unused. No whitespace before it, so the unused markup is unchanged. --><slot name="cue"></slot>
  </component>

</template>

<script>

import {settings} from '@/components/Data'
import {pickerValueFormat} from '@/lib/appTime'

import DateMixins from '@/mixins/DateMixins'

export default {
  name: 'date-input',

  mixins: [DateMixins],

  props: {
    label: {
      type: String,
      default: null
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    align: {
      type: String,
      default: 'top'
    },
    value: {
//      type: Date,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'date'
    },
    format: {
      type: String,
      default: 'dd MMM yyyy'
    },
    options: {
      type: Object,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    helpText: {
      type: String,
      default: null
    },
    prop: {
      type: String,
      default: null
    },
    noForm: {
      type: Boolean,
      default: false
    },
    // XOL-467: forwarded straight to el-form-item's own `error` prop, which
    // needs no `model`, no `prop` registration and no el-form `validate()` —
    // see docs/plans/2026-09-01-xol-467-date-chain-field-validation.md for
    // why Element's real validate() is unusable on this stack. Inert under
    // `:no-form` (the wrapper becomes a plain `div`, which has no such prop)
    // — by design; those callers (AuditReconcileCell) render the message as
    // plain text instead.
    error: {
      type: String,
      default: null
    },
    // XOL-67 (B8), additive: the accessible name of the picker's inner <input> ("Handover date"). el-date-picker passes no attribute
    // through, so it is set on the element itself and kept there (Element re-renders that <input>, and Vue's patch would drop an
    // aria-label it did not put there), as SelectOrganisation does (decision 71). Inert without it.
    ariaLabel: {
      type: String,
      default: null
    },
    // XOL-67 (B8), additive: `aria-invalid="true"` and `aria-describedby` on the same inner <input> (the form item's own message has no id,
    // so a screen reader does not hear it), kept there the same way. Inert without them.
    invalid: {
      type: Boolean,
      default: false
    },
    describedBy: {
      type: String,
      default: null
    }
  },
  computed: {
    dateFormat: function () {
      return this.format ? this.format : settings.selectDateFormat
    },

    // XOL-414: emit a wire-shaped string, not a JS Date. `format` above is the
    // display mask; this is the v-model shape.
    valueFormat: function () {
      return pickerValueFormat(this.localType)
    }
  },

  methods: {
    updated (value) {
      this.$emit('input', value)
      this.$emit('change', value)
    },

    applyInputAttrs () {
      if (!this.ariaLabel && !this.invalid && !this.describedBy) {
        return
      }

      var input = this.$el && typeof this.$el.querySelector === 'function' ? this.$el.querySelector('input') : null

      if (!input) {
        return
      }

      this.watchInput(input)

      // Setting an attribute to the value it has is still a mutation record: only touch what differs.
      var set = function (name, value) {
        if (value) {
          if (input.getAttribute(name) !== value) {
            input.setAttribute(name, value)
          }
        } else if (input.hasAttribute(name)) {
          input.removeAttribute(name)
        }
      }

      if (this.ariaLabel) {
        set('aria-label', this.ariaLabel)
      }

      set('aria-invalid', this.invalid ? 'true' : null)
      set('aria-describedby', this.invalid ? this.describedBy : null)
    },

    watchInput (input) {
      var self = this

      if (this.watchedInput === input || typeof MutationObserver === 'undefined') {
        return
      }

      this.unwatchInput()
      this.watchedInput = input
      this.attrObserver = new MutationObserver(function () { self.applyInputAttrs() })
      this.attrObserver.observe(input, { attributes: true, attributeFilter: ['aria-label', 'aria-invalid', 'aria-describedby'] })
    },

    unwatchInput () {
      if (this.attrObserver) {
        this.attrObserver.disconnect()
      }

      this.attrObserver = null
      this.watchedInput = null
    }
  },

  mounted () {
    this.applyInputAttrs()
  },

  // The `updated` LIFECYCLE hook: a changed `ariaLabel`, or the picker coming back from disabled, re-renders this component.
  updated () {
    this.applyInputAttrs()
  },

  beforeDestroy () {
    this.unwatchInput()
  },

  watch: {
    value: function (val) {
      this.formData = val
    }
  },


  data () {
    return {
      ref: 'date-' + Date.now() + '-' + Math.random(),
      localType: this.type ? this.type : 'date',
      formData: this.value,
      dateOptions: this.options,
      el: this.noForm ? 'div' : 'el-form-item'
    }
  }
}
</script>
